import React, { useContext, useEffect, useState } from "react";
import {
	SalesActivityModuleState,
	useSalesActivityModuleDispatch,
	useSalesActivityModuleSelector
} from "../../../../store";
import ZTaskDrawer from "../../../../templates/ZDrawers/ZSalesActivities/ZTask";
import Form from "antd/lib/form";
import {
	DefaultErrorMessage,
	FormKeys,
	ZomentumEntities
} from "@/Contracts";
import {
	AddTaskRequest,
	ESalesActivityType,
	SalesActivity,
	TaskFormValues
} from "@/Contracts/Others/SalesActivity";
import SalesActivityActionCreators from "../../../../store/actions/SalesActivity";
import ZMessage from "@molecules/ZMessage";
import { IAddSalesActivityDrawerProps } from "../interface";
import moment from "moment";
import SalesActivityAddWrapper from "../Wrappers/AddWrapper";
import SalesActivityEditWrapper from "../Wrappers/EditWrapper";
import {
	IOnlyTaskUIProps,
	ITaskUIProps
} from "../../../../templates/ZDrawers/ZSalesActivities/interface";
import useDatePattern from "@/Hooks/useDatePattern";
import { ParentContext } from "../../../../store/context/ParentContext";
import ZModalConfirm from "@atoms/ZModalConfirmation";
import { isPartnerApp } from "@/Utils";

const Task: React.FC<IAddSalesActivityDrawerProps> = ({
	visible = false,
	isEditMode = false,
	onClose,
	onSubmit
}) => {
	const [isCrossAssign, setIsCrossAssign] = useState(false);
	const parentContext = useContext(ParentContext);
	const { currentActiveSalesActivity } = useSalesActivityModuleSelector(
		(state: SalesActivityModuleState) => {
			return {
				currentActiveSalesActivity:
					state.salesActivity.currentActiveSalesActivity
			};
		}
	);
	const { userData } = parentContext.user;

	const [form] = Form.useForm();
	const dispatch = useSalesActivityModuleDispatch();

	const datePattern = useDatePattern();

	const { isFormEdited } = parentContext.setting;

	useEffect(() => {
		if (visible) {
			if (
				isEditMode &&
				currentActiveSalesActivity &&
				currentActiveSalesActivity.feed_details
			) {
				const feed = currentActiveSalesActivity.feed_details;
				form.setFieldsValue({
					title: feed.title,
					description: feed.description,
					date: moment(feed.date_time),
					time: moment(feed.date_time),
					task_type: feed.task_type,
					priority: feed.priority,
					collaborator_ids: feed.collaborator_ids,
					relation:
						currentActiveSalesActivity?.feed_details.entity_type,
					author_id: feed.author_id,
					is_marked_completed: feed?.is_marked_completed,
					completed_at: feed?.completed_at
						? moment(feed?.completed_at)
						: null
				});
				setIsCrossAssign(checkIfCrossAssign(feed.author_id));
			}
		} else {
			setIsCrossAssign(false);
		}
	}, [visible, currentActiveSalesActivity]);

	const handleClose = () => {
		form.resetFields();
		onClose && onClose();
		parentContext.setting.resetFormEdited?.(FormKeys.AddEditTask);
	};

	const closeTaskDrawer = () => {
		if (!isFormEdited?.[FormKeys.AddEditTask]) {
			handleClose();
		} else {
			ZModalConfirm({
				content: `Are you sure you want to discard ${
					isEditMode ? "editing" : "adding"
				} task?`,
				onOk: handleClose
			});
		}
	};

	const getFormValues = (addTaskFormValues: TaskFormValues) => {
		const addTaskRequest = new AddTaskRequest();
		addTaskRequest.title = addTaskFormValues.title;
		addTaskRequest.description = addTaskFormValues.description;
		const dateTime = new Date();
		dateTime.setDate(1);
		dateTime.setFullYear(addTaskFormValues.date.year());
		dateTime.setMonth(addTaskFormValues.date.month());
		dateTime.setDate(addTaskFormValues.date.date());
		dateTime.setHours(addTaskFormValues.time.hours());
		dateTime.setMinutes(addTaskFormValues.time.minutes());
		dateTime.setSeconds(0);
		dateTime.setMilliseconds(0);
		addTaskRequest.date_time = dateTime;
		addTaskRequest.author_id = addTaskFormValues.author_id ?? userData?.id;
		addTaskRequest.task_type = addTaskFormValues.task_type;
		addTaskRequest.priority = addTaskFormValues.priority;
		addTaskRequest.entity_type = addTaskFormValues.relation;
		addTaskRequest.collaborator_ids =
			addTaskFormValues.collaborator_ids || new Array<string>();
		addTaskRequest.is_marked_completed =
			addTaskFormValues.is_marked_completed
				? addTaskFormValues.is_marked_completed
				: false;
		addTaskRequest.completed_at = addTaskFormValues.completed_at
			? addTaskFormValues.completed_at.toDate()
			: null;

		switch (addTaskFormValues.relation) {
			case ZomentumEntities.Opportunity:
				addTaskRequest.entity_id = addTaskFormValues.opportunity;
				break;
			case ZomentumEntities.Client:
				addTaskRequest.entity_id = addTaskFormValues.client_company;
				break;
			case ZomentumEntities.VendorClient:
				addTaskRequest.entity_id =
					addTaskFormValues.vendor_client_company;
				break;
			case ZomentumEntities.VendorCompany:
				addTaskRequest.entity_id = addTaskFormValues.company;
				break;
			case ZomentumEntities.VendorOpportunity:
				addTaskRequest.entity_id = addTaskFormValues.vendor_opportunity;
				break;
			default:
				return addTaskRequest;
		}
		return addTaskRequest;
	};

	const addTask = async (addTaskFormValues: TaskFormValues) => {
		dispatch(SalesActivityActionCreators.showSalesActivityLoader());
		try {
			const addTaskRequest = getFormValues(addTaskFormValues);
			const addTaskResponse = await dispatch(
				SalesActivityActionCreators.addTask(
					addTaskRequest,
					isCrossAssign
				)
			);
			if (onSubmit) {
				onSubmit(
					addTaskResponse.addTask,
					form.getFieldValue(ZomentumEntities.Opportunity)
				);
			}
			handleClose();
			ZMessage.success("Task added successfully");
		} catch (error) {
			console.error(error);
		} finally {
			dispatch(SalesActivityActionCreators.hideSalesActivityLoader());
		}
	};

	const editTask = async (editTaskFormValues: TaskFormValues) => {
		dispatch(SalesActivityActionCreators.showSalesActivityLoader());
		try {
			if (
				currentActiveSalesActivity &&
				currentActiveSalesActivity.feed_details
			) {
				const editTaskRequest: SalesActivity = {
					...currentActiveSalesActivity
				};
				const formValues = getFormValues(editTaskFormValues);
				editTaskRequest.feed_details = {
					...currentActiveSalesActivity.feed_details,
					...formValues
				};
				const editTaskResponse = await dispatch(
					SalesActivityActionCreators.editTask(editTaskRequest)
				);
				if (onSubmit) {
					onSubmit(
						editTaskResponse.editTask,
						form.getFieldValue(ZomentumEntities.Opportunity)
					);
				}
				handleClose();
				ZMessage.success("Task edited successfully");
			} else {
				ZMessage.error(DefaultErrorMessage);
			}
		} catch (error) {
			console.error(error);
		} finally {
			dispatch(SalesActivityActionCreators.hideSalesActivityLoader());
		}
	};

	const handleFormChange = () => {
		!isFormEdited?.[FormKeys.AddEditTask] &&
			parentContext.setting.setFormEdited?.(FormKeys.AddEditTask);
	};

	const handleOwnerChange = (value: string) => {
		setIsCrossAssign(checkIfCrossAssign(value));
	};

	const checkIfCrossAssign = (selectedUserId: string) => {
		const user = parentContext.user.companyUserList.find(
			user => user.id === selectedUserId
		);
		if (!user) {
			form.setFieldsValue({ collaborator_ids: [] });
			return true;
		} else {
			return false;
		}
	};

	const isEditable = !!currentActiveSalesActivity?.feed_details
		?.sharing_details
		? currentActiveSalesActivity.feed_details.sharing_details.is_editable
		: true;

	const addTaskUIProps: IOnlyTaskUIProps = {
		onSubmit: isEditMode ? editTask : addTask,
		dateFormat: datePattern,
		handleFormChange,
		handleOwnerChange,
		isCrossAssign,
		isReadOnly: !isEditable,
		isEditMode
	};

	return isEditMode ? (
		<SalesActivityEditWrapper<ITaskUIProps, IOnlyTaskUIProps>
			visible={visible}
			onClose={closeTaskDrawer}
			Component={ZTaskDrawer}
			form={form}
			formUIProps={addTaskUIProps}
			salesActivityType={ESalesActivityType.TASK}
		/>
	) : (
		<SalesActivityAddWrapper<ITaskUIProps, IOnlyTaskUIProps>
			visible={visible}
			onClose={closeTaskDrawer}
			Component={ZTaskDrawer}
			form={form}
			formUIProps={addTaskUIProps}
			salesActivityType={ESalesActivityType.TASK}
		/>
	);
};

export default Task;
